import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { posts } from "@/mocks/posts";
import { topics } from "@/mocks/topics";
import { comments } from "@/mocks/comments";
import type { CommentItem, Post, Topic } from "@/types/content";
import CommentList from "./components/CommentList";
import RelatedPosts from "./components/RelatedPosts";

const allPosts = posts as Post[];
const allTopics = topics as Topic[];
const allComments = comments as CommentItem[];

export default function PostDetail() {
  const { id } = useParams();
  const post = allPosts.find((p) => p.id === id);

  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const postComments = useMemo(
    () => allComments.filter((c) => c.postId === id),
    [id],
  );

  const related = useMemo(() => {
    if (!post) return [];
    return allPosts.filter((p) => p.id !== post.id && p.category === post.category).slice(0, 3);
  }, [post]);

  const boardHot = useMemo(() => {
    if (!post) return [];
    return allPosts
      .filter((p) => p.category === post.category && p.id !== post.id)
      .sort((a, b) => b.likes - a.likes)
      .slice(0, 3);
  }, [post]);

  const siteHot = useMemo(
    () => [...allPosts].sort((a, b) => a.hot - b.hot).slice(0, 5),
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!post) {
    return (
      <SiteLayout>
        <PageMeta
          title="帖子不存在 - 高校吃瓜网"
          description="你访问的帖子可能已被合并或下架，返回讨论区继续浏览其他热门讨论。"
          keywords="高校吃瓜,帖子不存在,讨论区"
          path="/topics"
        />
        <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">
            这篇帖子找不到了
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            它可能已被合并到其他话题，或因为内容调整暂时下架。去讨论区看看其他正在更新的帖子吧。
          </p>
          <Link
            to="/topics"
            className="mt-6 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回讨论区
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = allTopics.find((t) => t.slug === post.category);
  const categoryName = category?.name ?? "讨论";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        datePublished: post.createdAt,
        dateModified: post.createdAt,
        author: { "@type": "Person", name: post.author },
        publisher: { "@type": "Organization", name: "高校吃瓜网" },
        keywords: post.tags.join(","),
        commentCount: post.comments,
        articleSection: categoryName,
        url: `https://www.gaoxiaochigua.cn/post/${post.id}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://www.gaoxiaochigua.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "讨论区",
            item: "https://www.gaoxiaochigua.cn/topics",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: categoryName,
            item: `https://www.gaoxiaochigua.cn/topics?category=${post.category}`,
          },
          { "@type": "ListItem", position: 4, name: post.title },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <PageMeta
        title={`${post.title} - 高校吃瓜网`}
        description={post.excerpt}
        keywords={`${post.school},${categoryName},${post.tags.join(",")},高校吃瓜`}
        path={`/post/${post.id}`}
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </PageMeta>

      <div className="mx-auto w-full px-4 pb-16 pt-8 md:px-8 md:pb-20 md:pt-12">
        <Breadcrumbs
          items={[
            { label: "讨论区", to: "/topics" },
            { label: categoryName, to: `/topics?category=${post.category}` },
            { label: post.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px] lg:gap-12">
          <article className="min-w-0">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/topics?category=${post.category}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={category?.icon ?? "ri-price-tag-3-line"}></i>
                  </span>
                  {categoryName}
                </Link>
                <Link
                  to={`/topics?school=${encodeURIComponent(post.school)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-map-pin-2-line"></i>
                  </span>
                  {post.school}
                </Link>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-[40px]">
                {post.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600 md:text-sm">
                <span className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className="ri-user-3-line text-xs"></i>
                  </span>
                  <span className="text-foreground-900">{post.author}</span>
                  <span className="text-foreground-500">{post.authorRole}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {post.createdAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {post.views} 次阅读
                </span>
              </div>
            </header>

            <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
              <img
                src={post.cover}
                alt={`${post.title} - ${post.school}${categoryName}话题配图`}
                title={`${post.title} 高校吃瓜网`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 flex flex-col gap-5">
              {post.body.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-[1.9] text-foreground-800 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <blockquote className="mt-8 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5 text-[15px] font-medium italic leading-relaxed text-foreground-800 md:text-base">
              {post.quote}
            </blockquote>

            <section aria-label="信息要点" className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-list-check-2"></i>
                </span>
                信息要点
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {post.points.map((point) => (
                  <li key={point} className="flex gap-3 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                    {point}
                  </li>
                ))}
              </ul>
            </section>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 md:text-sm"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-background-200 py-5">
              <button
                type="button"
                onClick={() => setLiked((v) => !v)}
                aria-pressed={liked}
                className={`flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  liked
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600"
                    : "border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-700"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                </span>
                {liked ? "已点赞" : "有用"} {post.likes + (liked ? 1 : 0)}
              </button>

              <button
                type="button"
                onClick={() => setSaved((v) => !v)}
                aria-pressed={saved}
                className={`flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  saved
                    ? "bg-secondary-500 text-foreground-950 hover:bg-secondary-600"
                    : "border border-background-300 text-foreground-800 hover:border-secondary-400 hover:text-secondary-900"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={saved ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                </span>
                {saved ? "已收藏" : "收藏"}
              </button>

              <button
                type="button"
                onClick={handleCopy}
                className="flex cursor-pointer items-center gap-2 rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={copied ? "ri-check-line" : "ri-link"}></i>
                </span>
                {copied ? "链接已复制" : "复制链接"}
              </button>

              <a
                href="#comments"
                className="ml-auto flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700 whitespace-nowrap"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-chat-3-line"></i>
                </span>
                {post.comments} 条评论
              </a>
            </div>

            {postComments.length > 0 ? (
              <CommentList items={postComments} total={post.comments} />
            ) : (
              <section id="comments" className="mt-12 rounded-lg border border-dashed border-background-300 p-6 text-center">
                <h2 className="font-heading text-base font-bold text-foreground-950">还没有精选评论</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  这条帖子的讨论正在整理中，稍后再来看看，或去讨论区参与其他话题。
                </p>
              </section>
            )}

            <RelatedPosts items={related} />
          </article>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:h-fit">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-bookmark-3-line"></i>
                </span>
                本板块热门
              </h2>
              <ul className="mt-4 flex flex-col gap-3.5">
                {boardHot.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/post/${item.id}`} className="group flex gap-3">
                      <span className="font-heading text-sm font-black text-foreground-300">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {item.likes} 赞 · {item.comments} 评论
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-fire-line"></i>
                </span>
                全站热搜榜
              </h2>
              <ol className="mt-4 flex flex-col gap-3.5">
                {siteHot.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/post/${item.id}`} className="group flex gap-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-black ${
                          index < 3
                            ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                        <span className="line-clamp-2">{item.title}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-secondary-500 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                想聊你学校的事？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-800">
                进入讨论区，按学校筛选就能看到同校正在更新的帖子，也欢迎给我们投稿。
              </p>
              <Link
                to="/topics"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
              >
                <i className="ri-compass-3-line text-base"></i>
                去讨论区
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}